import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  Typography,
  Tabs,
  Tab,
  FormControl,
  FormLabel,
  TextField
} from '@mui/material';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTranslation } from 'react-i18next';
import ReactToPrint from 'react-to-print';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../api/firebase';
import { useAppSelector } from '../../utils/hooks';
import { customersData } from '../../store/reducer/customersSlice';
import { productsData } from '../../store/reducer/productsSlice';
import CustomAutocomplete from '../../components/AutoComplete';
import { CustomersAutoComplete } from '../../components/AutoComplete/Customers';
import { SelectMonthAutoComplete } from '../../components/AutoComplete/SelectMonth';
import RadioButton from '../../components/RadioButton';
import OverlayLoader from '../../components/modals/OverlayLoader';
import InvoiceModal from '../../components/modals/InvoiceModal';
import SalesReportTable from './SalesReportTable';
import SalesReportChart from './SalesReportChart';
import {
  parseMonthYear,
  processReportData,
  extractCategoriesFromProducts,
  extractTypesFromProducts
} from '../../utils/salesReportHelpers';

/**
 * Sales Report Page Component
 */
export default function SalesReport() {
  const isMediumScreen = useMediaQuery('(min-width: 960px)');
  const { t } = useTranslation();
  const componentRef = useRef();

  // Redux state
  const { customers } = useAppSelector(customersData);
  const { products } = useAppSelector(productsData);

  // Filter states
  const [customerType, setCustomerType] = useState('all');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [periodType, setPeriodType] = useState('daily');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedMonthYear, setSelectedMonthYear] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');

  // Data states
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // PDF Modal states
  const [openInvoice, setOpenInvoice] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);

  // Options for dropdowns
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);

  // Initialize categories and types from products
  useEffect(() => {
    const extractedCategories = extractCategoriesFromProducts(products);
    const extractedTypes = extractTypesFromProducts(products);
    
    setCategories(['all', ...extractedCategories]);
    setTypes(['all', ...extractedTypes]);

    // Set default month/year
    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();
    const monthStr = currentMonth.toString().padStart(2, '0');
    setSelectedMonthYear(`${monthStr}-${currentYear}`);
    
    console.log('Products loaded:', products.length);
    console.log('Categories:', extractedCategories);
    console.log('Types:', extractedTypes);
  }, [products]);

  /**
   * Fetch daily report data from Firestore
   * Structure: Order/{date}/{customer_id}/delivered
   */
  const fetchDailyReport = async (date, customerId, category, type) => {
    try {
      const dateStr = typeof date === 'string' ? date : date.toISOString().split('T')[0];
      
      console.log('Fetching orders for date:', dateStr);
      console.log('Filters:', { customerId, category, type });
      
      let allOrders = [];
      
      // Determine which customers to query
      const customersToQuery = customerId === 'all' 
        ? customers 
        : customers.filter(c => c.id === customerId);
      
      console.log('Querying for', customersToQuery.length, 'customers');
      
      // Query each customer's "delivered" document under the date
      for (const customer of customersToQuery) {
        try {
          // Path: Order/{date}/{customer_id}/delivered
          const deliveredDocRef = doc(db, 'Order', dateStr, customer.id, 'delivered');
          const deliveredDoc = await getDoc(deliveredDocRef);
          
          if (deliveredDoc.exists()) {
            const docData = deliveredDoc.data();
            console.log(`Customer ${customer.id}: delivered document found`);
            
            // The order field contains the products array
            if (docData.order && Array.isArray(docData.order)) {
              console.log(`Customer ${customer.id}: ${docData.order.length} products in order`);
              
              // Filter products by category and type
              const filteredProducts = docData.order.filter(product => {
                // Category filter
                if (category && category !== 'all') {
                  if (product.category !== category) return false;
                }
                
                // Type filter
                if (type && type !== 'all') {
                  if (product.type !== type) return false;
                }
                
                return true;
              });
              
              if (filteredProducts.length > 0) {
                // Create an order object that matches expected structure
                const orderObj = {
                  id: deliveredDoc.id,
                  customer_id: customer.id,
                  delivered: docData.delivered,
                  modification: docData.modification,
                  data: docData.data || {}, // Include the data object with payment info
                  order: {
                    products: filteredProducts,
                    customer_id: customer.id
                  }
                };
                
                allOrders.push(orderObj);
                console.log(`Customer ${customer.id}: ${filteredProducts.length} products after filtering`);
              }
            }
          }
        } catch (error) {
          console.warn(`Error fetching delivered doc for customer ${customer.id}:`, error.message);
        }
      }
      
      console.log('Total filtered orders:', allOrders.length);
      return allOrders;
    } catch (error) {
      console.error('Error fetching daily report:', error, error.message);
      return [];
    }
  };

  /**
   * Fetch monthly report data from Firestore
   * Structure: Order/{date}/{customer_id}/delivered
   */
  const fetchMonthlyReport = async (month, year, customerId, category, type) => {
    try {
      const monthNum = parseInt(month) - 1;
      const yearNum = parseInt(year);
      const startDate = new Date(yearNum, monthNum, 1);
      const endDate = new Date(yearNum, monthNum + 1, 0);
      
      console.log('Fetching monthly report from', startDate.toDateString(), 'to', endDate.toDateString());
      
      // Generate all dates in the month
      const dates = [];
      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        dates.push(d.toISOString().split('T')[0]); // YYYY-MM-DD format
      }
      
      console.log(`Generated ${dates.length} dates for the month`);
      
      let allOrders = [];
      
      // Determine which customers to query
      const customersToQuery = customerId === 'all' 
        ? customers 
        : customers.filter(c => c.id === customerId);
      
      console.log('Querying for', customersToQuery.length, 'customers');
      
      // Query each date and customer combination
      for (const dateStr of dates) {
        for (const customer of customersToQuery) {
          try {
            // Path: Order/{date}/{customer_id}/delivered
            const deliveredDocRef = doc(db, 'Order', dateStr, customer.id, 'delivered');
            const deliveredDoc = await getDoc(deliveredDocRef);
            
            if (deliveredDoc.exists()) {
              const docData = deliveredDoc.data();
              
              // The order field contains the products array
              if (docData.order && Array.isArray(docData.order)) {
                // Filter products by category and type
                const filteredProducts = docData.order.filter(product => {
                  // Category filter
                  if (category && category !== 'all') {
                    if (product.category !== category) return false;
                  }
                  
                  // Type filter
                  if (type && type !== 'all') {
                    if (product.type !== type) return false;
                  }
                  
                  return true;
                });
                
                if (filteredProducts.length > 0) {
                  // Create an order object that matches expected structure
                  const orderObj = {
                    id: `${dateStr}_${customer.id}`,
                    customer_id: customer.id,
                    delivered: docData.delivered,
                    modification: docData.modification,
                    data: docData.data || {}, // Include the data object with payment info
                    order: {
                      products: filteredProducts,
                      customer_id: customer.id
                    }
                  };
                  
                  allOrders.push(orderObj);
                }
              }
            }
          } catch (error) {
            // Document might not exist for some date/customer combinations
            // This is normal, so we don't log it
          }
        }
      }
      
      console.log('Total orders from monthly fetch:', allOrders.length);
      return allOrders;
    } catch (error) {
      console.error('Error fetching monthly report:', error);
      return [];
    }
  };

  /**
   * Handle View Reports button click
   */
  const handleViewReports = async () => {
    console.log('=== Starting View Reports ===');
    setLoading(true);
    setReportData([]);

    try {
      let rawOrders = [];
      
      // Determine customer ID
      const customerId = customerType === 'all' ? 'all' : selectedCustomer?.id;
      
      console.log('Report Parameters:', {
        periodType,
        selectedDate,
        selectedMonthYear,
        customerId,
        selectedCategory,
        selectedType
      });
      
      // Fetch based on period type
      if (periodType === 'daily') {
        rawOrders = await fetchDailyReport(
          selectedDate,
          customerId,
          selectedCategory,
          selectedType
        );
      } else {
        const { month, year } = parseMonthYear(selectedMonthYear);
        console.log('Parsed month/year:', { month, year });
        rawOrders = await fetchMonthlyReport(
          month,
          year,
          customerId,
          selectedCategory,
          selectedType
        );
      }

      console.log('Raw orders fetched:', rawOrders.length);
      console.log('Sample order:', rawOrders[0]);

      // Process data
      const processed = processReportData(rawOrders, products, customers);
      console.log('Processed data:', processed.length);
      console.log('Sample processed:', processed[0]);
      
      setReportData(processed);
    } catch (error) {
      console.error('Error generating report:', error, error.stack);
    } finally {
      setLoading(false);
      console.log('=== View Reports Complete ===');
    }
  };

  /**
   * Format data for PDF Invoice - Aggregate products by ID
   */
  const formatDataForInvoice = () => {
    if (reportData.length === 0) return null;

    // Get customer info
    const customer = customerType === 'all' 
      ? { name_english: 'All Customers', name_urdu: 'تمام صارفین' }
      : selectedCustomer;

    // Aggregate products by product ID
    const productMap = {};
    let totalDeliveryCharges = 0;
    let totalAmountPaid = 0;
    let totalBillAmount = 0;
    let totalGrandTotal = 0;
    let ordersWithData = 0;
    let ordersWithoutData = 0;
    
    reportData.forEach(order => {
      // Extract payment info from the raw data
      if (order.rawData && order.rawData.data && Object.keys(order.rawData.data).length > 0) {
        const dataObj = order.rawData.data;
        totalDeliveryCharges += parseFloat(dataObj.delivery_charges || 0);
        totalAmountPaid += parseFloat(dataObj.amountPaid || 0);
        totalBillAmount += parseFloat(dataObj.bill || 0);
        totalGrandTotal += parseFloat(dataObj.grandTotal || 0);
        ordersWithData++;
        
        console.log('Found payment data for order:', {
          orderId: order.orderId,
          customerId: dataObj.customerId,
          delivery_charges: dataObj.delivery_charges,
          amountPaid: dataObj.amountPaid,
          bill: dataObj.bill,
          grandTotal: dataObj.grandTotal
        });
      } else {
        ordersWithoutData++;
        console.log('No payment data found for order:', order.orderId);
      }
      
      order.products.forEach(product => {
        const productId = product.id || product.product_id || product.name_english;
        
        if (!productMap[productId]) {
          // First time seeing this product
          productMap[productId] = {
            id: product.id,
            product_id: product.product_id,
            category_english: product.category_english || product.category || '',
            category_urdu: product.category_urdu || '',
            type_english: product.type_english || product.type || '',
            type_urdu: product.type_urdu || '',
            name_english: product.name_english || product.name || '',
            name_urdu: product.name_urdu || '',
            price: product.price || 0,
            quantity: 0,
            recipe: product.recipe || '',
            unit: product.unit || ''
          };
        }
        
        // Add quantity to existing product
        productMap[productId].quantity += parseFloat(product.quantity || 0);
      });
    });

    // Convert map to array and sort by product ID
    const aggregatedProducts = Object.values(productMap).sort((a, b) => {
      const idA = parseInt(a.id || a.product_id || 999999);
      const idB = parseInt(b.id || b.product_id || 999999);
      return idA - idB;
    });

    // Calculate total bill from aggregated products
    const totalBill = aggregatedProducts.reduce((sum, product) => {
      return sum + (product.quantity * product.price);
    }, 0);

    // Calculate balance (total bill + delivery charges - amount paid)
    const grandTotal = totalBill + totalDeliveryCharges;
    const balance = grandTotal - totalAmountPaid;

    console.log('=== PDF Invoice Summary ===');
    console.log('Total orders processed:', reportData.length);
    console.log('Orders with payment data:', ordersWithData);
    console.log('Orders without payment data:', ordersWithoutData);
    console.log('Aggregated products:', aggregatedProducts.length);
    console.log('Payment info:', { 
      totalBill, 
      totalDeliveryCharges, 
      totalAmountPaid, 
      grandTotal, 
      balance 
    });
    console.log('Sample aggregated product:', aggregatedProducts[0]);
    console.log('=========================');

    // Create invoice object matching DashboardReports format
    const invoice = {
      id: customer.id || 'all',
      invoice_no: `SR-${periodType === 'daily' ? selectedDate.toISOString().split('T')[0] : selectedMonthYear}`,
      name_urdu: customer.name_urdu || '',
      name_english: customer.name_english || '',
      delivery_charges: totalDeliveryCharges,
      address_english: customer.address_english || '',
      address_urdu: customer.address_urdu || '',
      city_english: customer.city_english || '',
      city_urdu: customer.city_urdu || '',
      date: periodType === 'daily' 
        ? selectedDate.toISOString().split('T')[0] 
        : selectedMonthYear,
      due_date: '',
      preparedBy: '',
      ArrearData: 0,
      ArrearPayment: 0,
      bill: totalBill,
      pay: totalAmountPaid,
      balance: balance,
      advancePayment: 0,
      items: aggregatedProducts,
      returnAmount: 0,
      payment: totalAmountPaid,
      username: ''
    };

    return invoice;
  };

  /**
   * Handle View PDF button click
   */
  const handleViewPDF = () => {
    const invoice = formatDataForInvoice();
    if (invoice) {
      setInvoiceData(invoice);
      setOpenInvoice(true);
    }
  };

  /**
   * Event handlers
   */
  const handleCustomerTypeChange = (event) => {
    setCustomerType(event.target.value);
    if (event.target.value === 'all') {
      setSelectedCustomer(null);
    }
  };

  const handlePeriodTypeChange = (event) => {
    setPeriodType(event.target.value);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleDateChange = (event) => {
    const date = new Date(event.target.value);
    setSelectedDate(date);
  };

  const formatDateForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleInvoiceModal = () => {
    setOpenInvoice(false);
  };

  return (
    <Box>
      <Grid container spacing={2}>
        {/* Filters Card */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                {t('Sales Report')}
              </Typography>

              {/* Customer Selection */}
              <Box sx={{ mb: 3 }}>
                <FormLabel component="legend" sx={{ mb: 1 }}>
                  Customer Selection
                </FormLabel>
                <RadioButton
                  val={customerType}
                  handleChange={handleCustomerTypeChange}
                  option1="all"
                  option2="selected"
                />
                {customerType === 'selected' && (
                  <Box sx={{ mt: 2 }}>
                    <CustomersAutoComplete
                      value={selectedCustomer}
                      onChange={(event, newValue) => setSelectedCustomer(newValue)}
                    />
                  </Box>
                )}
              </Box>

              {/* Period Selection */}
              <Box sx={{ mb: 3 }}>
                <FormLabel component="legend" sx={{ mb: 1 }}>
                  Report Period
                </FormLabel>
                <RadioButton
                  val={periodType}
                  handleChange={handlePeriodTypeChange}
                  option1="daily"
                  option2="monthly"
                />
                
                {periodType === 'daily' ? (
                  <Box sx={{ mt: 2 }}>
                    <TextField
                      type="date"
                      label="Select Date"
                      value={formatDateForInput(selectedDate)}
                      onChange={handleDateChange}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      sx={{ maxWidth: 350 }}
                    />
                  </Box>
                ) : (
                  <Box sx={{ mt: 2 }}>
                    <SelectMonthAutoComplete
                      value={selectedMonthYear}
                      onChange={(event, newValue) => setSelectedMonthYear(newValue)}
                    />
                  </Box>
                )}
              </Box>

              {/* Category and Type Selection */}
              <Box sx={{ mb: 3 }}>
                <Stack direction={isMediumScreen ? 'row' : 'column'} spacing={2}>
                  <FormControl fullWidth>
                    <CustomAutocomplete
                      options={categories}
                      value={selectedCategory}
                      onChange={(event, newValue) => setSelectedCategory(newValue || 'all')}
                      label="Select Category"
                    />
                  </FormControl>
                  
                  <FormControl fullWidth>
                    <CustomAutocomplete
                      options={types}
                      value={selectedType}
                      onChange={(event, newValue) => setSelectedType(newValue || 'all')}
                      label="Select Type"
                    />
                  </FormControl>
                </Stack>
              </Box>

              {/* Action Buttons */}
              <Box sx={{ textAlign: 'center' }}>
                <Stack direction={isMediumScreen ? 'row' : 'column'} spacing={2} justifyContent="center">
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleViewReports}
                    disabled={loading}
                    sx={{ minWidth: 200 }}
                  >
                    {loading ? 'Loading...' : 'View Reports'}
                  </Button>
                  
                  {reportData.length > 0 && (
                    <Button
                      variant="contained"
                      color="success"
                      size="large"
                      onClick={handleViewPDF}
                      sx={{ minWidth: 200 }}
                    >
                      View PDF Report (Urdu)
                    </Button>
                  )}
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Report Display */}
        {reportData.length > 0 && (
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                {/* Print Button */}
                <Box sx={{ mb: 2, textAlign: 'right' }}>
                  <ReactToPrint
                    trigger={() => (
                      <Button variant="outlined">
                        {t('Print Report')}
                      </Button>
                    )}
                    content={() => componentRef.current}
                  />
                </Box>

                {/* Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tabs value={tabValue} onChange={handleTabChange}>
                    <Tab label="Summary" />
                    <Tab label="Detailed Table" />
                  </Tabs>
                </Box>

                {/* Tab Content */}
                <Box ref={componentRef}>
                  {tabValue === 0 && <SalesReportChart data={reportData} />}
                  {tabValue === 1 && <SalesReportTable data={reportData} />}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
        
        {/* No Data Message */}
        {!loading && reportData.length === 0 && (
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No Data Found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Click "View Reports" to load data. Check the browser console for debugging information.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Loading Overlay */}
      <OverlayLoader open={loading} />

      {/* Invoice PDF Modal */}
      {invoiceData && (
        <InvoiceModal
          openInvoice={openInvoice}
          orderByCustomer={invoiceData}
          setOpenInvoice={handleInvoiceModal}
        />
      )}
    </Box>
  );
}

